import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'
import DashboardHeader from '../components/DashboardHeader'
import TopCompetitors from '../components/TopCompetitors'
import ShareOfVoiceCard from '../components/ShareOfVoiceCard'
import SentimentCard from '../components/SentimentCard'
import PositionCard from '../components/PositionCard'
import SearchIntelligenceOverview from '../components/SearchIntelligenceOverview'
import ExecutiveSummaryInsights from '../components/ExecutiveSummaryInsights'
import {
  MetricAvailabilityChecker
} from '../utils/metricAvailability.js'
import { RecommendationEngine } from '../utils/recommendationEngine'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import {
  demoDashboardStats,
  demoCompetitorScores
} from '../utils/demoData'
import {
  Lightbulb,
  ArrowRight,
  Globe,
  Zap
} from 'lucide-react'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer
} from 'recharts'

export default function Dashboard() {
  const { currentUser, activeBrand } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [competitorScores, setCompetitorScores] = useState<any[]>([])
  const [loadingCompetitors, setLoadingCompetitors] = useState(false)
  const [headerStats, setHeaderStats] = useState<{
    aiIndexScore: number | undefined
    aiMentionRate: number | undefined
    trackedQueries: number | undefined
    competitorCount: number | undefined
  }>({
    aiIndexScore: undefined,
    aiMentionRate: undefined,
    trackedQueries: undefined,
    competitorCount: undefined
  })

  // User configuration for availability checking
  const [userConfig, setUserConfig] = useState<{
    hasLlmsTxt: boolean
    llmsTxtLastUpdated: Date | undefined
    trackedQueries: number
    dataHistoryMonths: number
    aiProvidersConfigured: any[]
    integrationsEnabled: {
      analytics: boolean
      crm: boolean
      revenue: boolean
    }
    accountTier: any
    contentCount: number
    enrichedContentCount: number
  }>({
    hasLlmsTxt: false,
    llmsTxtLastUpdated: undefined,
    trackedQueries: 0,
    dataHistoryMonths: 0,
    aiProvidersConfigured: [],
    integrationsEnabled: {
      analytics: false,
      crm: false,
      revenue: false
    },
    accountTier: 'starter',
    contentCount: 0,
    enrichedContentCount: 0
  })

  useEffect(() => {
    // Track page view
    if (isDemoMode) {
      trackEvent(ANALYTICS_EVENTS.DEMO_VIEWED)
    } else {
      trackEvent(ANALYTICS_EVENTS.DASHBOARD_VIEWED)
    }

    fetchDashboardData()
    fetchUserConfiguration()
    fetchTopCompetitors()
    fetchHeaderStats()
    // Note: onboarding check removed - now centralized in PrivateRoute
  }, [activeBrand])

  const fetchUserConfiguration = async () => {
    try {
      // This would be an API call to get user's actual configuration
      // const response = await api.get(`/user/configuration/${activeBrand?.id}`)

      // For now, using mock data based on what might be available
      setUserConfig({
        hasLlmsTxt: stats?.hasLlmsTxt || false,
        llmsTxtLastUpdated: stats?.llmsTxtLastUpdated ? new Date(stats.llmsTxtLastUpdated) : undefined,
        trackedQueries: stats?.trackedQueries || 0,
        dataHistoryMonths: stats?.dataHistoryMonths || 0,
        aiProvidersConfigured: stats?.aiProviders || [],
        integrationsEnabled: {
          analytics: stats?.hasAnalytics || false,
          crm: stats?.hasCRM || false,
          revenue: stats?.hasRevenue || false
        },
        accountTier: (currentUser?.accountTier || 'starter') as any,
        contentCount: 0,
        enrichedContentCount: 0
      })
    } catch (error) {
      console.error('Failed to fetch user configuration:', error)
    }
  }

  const fetchDashboardData = async () => {
    try {
      // Use demo data if in demo mode
      if (isDemoMode) {
        setStats(demoDashboardStats)
        setHeaderStats({
          aiIndexScore: 72,
          aiMentionRate: 65,
          trackedQueries: 24,
          competitorCount: 5
        })
        setUserConfig({
          hasLlmsTxt: demoDashboardStats.hasLlmsTxt,
          llmsTxtLastUpdated: undefined,
          trackedQueries: demoDashboardStats.trackedQueries,
          dataHistoryMonths: demoDashboardStats.dataHistoryMonths,
          aiProvidersConfigured: demoDashboardStats.aiProviders,
          integrationsEnabled: {
            analytics: demoDashboardStats.hasAnalytics,
            crm: demoDashboardStats.hasCRM,
            revenue: demoDashboardStats.hasRevenue
          },
          accountTier: (currentUser?.accountTier || 'starter') as any,
          contentCount: demoDashboardStats.contentCount,
          enrichedContentCount: demoDashboardStats.enrichedContentCount
        })
        setLoading(false)
        return
      }

      const response = await api.get('/dashboard/stats')
      setStats(response.data)

      // Update header stats from dashboard data
      if (response.data) {
        setHeaderStats(prev => ({
          ...prev,
          aiIndexScore: response.data.aiIndexScore ?? undefined,
          trackedQueries: response.data.trackedQueries ?? response.data.searchQueries ?? undefined
        }))

        setUserConfig({
          hasLlmsTxt: response.data.hasLlmsTxt || false,
          llmsTxtLastUpdated: response.data.llmsTxtLastUpdated ? new Date(response.data.llmsTxtLastUpdated) : undefined,
          trackedQueries: response.data.trackedQueries || 0,
          dataHistoryMonths: response.data.dataHistoryMonths || 0,
          aiProvidersConfigured: response.data.aiProviders || [],
          integrationsEnabled: {
            analytics: response.data.hasAnalytics || false,
            crm: response.data.hasCRM || false,
            revenue: response.data.hasRevenue || false
          },
          accountTier: (currentUser?.accountTier || 'starter') as any,
          contentCount: response.data.contentCount || 0,
          enrichedContentCount: response.data.enrichedContentCount || 0
        })
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTopCompetitors = async () => {
    try {
      setLoadingCompetitors(true)

      // Use demo data if in demo mode
      if (isDemoMode) {
        setCompetitorScores(demoCompetitorScores)
        setLoadingCompetitors(false)
        return
      }

      if (!activeBrand?.id) {
        setLoadingCompetitors(false)
        return
      }

      // Fetch competitors list to get head-to-head flags
      const competitorsResponse = await api.get(`/competitors/${activeBrand.id}`)
      const scoresResponse = await api.get(`/competitors/${activeBrand.id}/scores`)

      if (competitorsResponse.data.success && scoresResponse.data.success) {
        const competitors = competitorsResponse.data.competitors
        const scores = scoresResponse.data.scores

        // Update competitor count in header
        setHeaderStats(prev => ({
          ...prev,
          competitorCount: competitors.length
        }))

        // Create a map of head-to-head flags
        const headToHeadMap = new Map(
          competitors.map((c: any) => [c.id, c.isHeadToHead])
        )

        // Filter scores to only head-to-head competitors
        const headToHeadScores = scores
          .filter((s: any) => headToHeadMap.get(s.id))
          .sort((a: any, b: any) => b.aiIndexScore - a.aiIndexScore)
          .slice(0, 3)

        setCompetitorScores(headToHeadScores)
      }
    } catch (error) {
      console.error('Failed to fetch competitors:', error)
    } finally {
      setLoadingCompetitors(false)
    }
  }

  const fetchHeaderStats = async () => {
    try {
      // Demo mode header stats are set in fetchDashboardData
      if (isDemoMode || !activeBrand?.id) return

      // Fetch mention rate from visibility data
      const visibilityRes = await api.get(`/visibility/${activeBrand.id}`).catch(() => null)

      if (visibilityRes?.data?.details?.aiMentions) {
        setHeaderStats(prev => ({
          ...prev,
          aiMentionRate: visibilityRes.data.details.aiMentions.mentionRate ?? undefined
        }))
      }
    } catch (error) {
      console.error('Failed to fetch header stats:', error)
    }
  }

  // Check availability for all metrics
  const availabilityChecker = new MetricAvailabilityChecker(userConfig)
  const setupProgress = availabilityChecker.getSetupProgress()

  // Generate recommendations
  const recommendationEngine = new RecommendationEngine({
    setupComplete: setupProgress.percentage >= 100,
    hasLlmsTxt: userConfig.hasLlmsTxt || false,
    llmsTxtPublished: false, // TODO: Check actual domain for LLMS.txt
    hasManifestJson: false, // TODO: Check for manifest.json
    contentCount: userConfig.contentCount || 0,
    enrichedContentCount: userConfig.enrichedContentCount || 0,
    searchQueriesCount: userConfig.trackedQueries || 0,
    hasBrandQueries: false, // TODO: Get from stats
    hasProductQueries: false, // TODO: Get from stats
    totalQueries: userConfig.trackedQueries || 0,
    llmsTxtLastUpdated: userConfig.llmsTxtLastUpdated,
    domainConfigured: !!activeBrand?.domain
  })
  const recommendations = recommendationEngine.getRecommendations()
  const recommendationCounts = recommendationEngine.getRecommendationCounts()

  // Mock data for charts
  const aiProviderData = [
    { name: 'OpenAI', value: 35, color: '#10B981' },
    { name: 'Anthropic', value: 28, color: '#6366F1' },
    { name: 'Google', value: 22, color: '#F59E0B' },
    { name: 'Others', value: 15, color: '#8B5CF6' },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <DashboardHeader
        stats={headerStats}
        loading={loading}
      />

      {/* AI Insights - shows upgrade prompt for STARTER/TRIAL, full insights for GROWTH/PRO */}
      <ExecutiveSummaryInsights />

      {/* Share of Voice, Sentiment, and Position Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ShareOfVoiceCard />
        <SentimentCard />
        <PositionCard />
      </div>

      {/* Head-to-Head Competitors */}
      <TopCompetitors
        competitors={competitorScores}
        loading={loadingCompetitors}
        limit={3}
      />

      {/* Search Intelligence Overview */}
      <SearchIntelligenceOverview />

      {/* Quick Actions for Unavailable Features
      {setupProgress.percentage < 50 && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold flex items-center">
                <Sparkles className="h-5 w-5 mr-2" />
                Unlock More Insights
              </h2>
              <p className="mt-1 text-indigo-100">
                Complete setup to access all metrics and features
              </p>
            </div>
            <div className="flex space-x-3">
              {!userConfig.hasLlmsTxt && (
                <Link
                  to="/llms"
                  className="px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors"
                >
                  Create LLMS.txt
                </Link>
              )}
              {userConfig.trackedQueries === 0 && (
                <Link
                  to="/search-tracking"
                  className="px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors"
                >
                  Track Queries
                </Link>
              )}
              {userConfig.aiProvidersConfigured.length === 0 && (
                <Link
                  to="/settings"
                  className="px-4 py-2 bg-white/20 text-white border border-white/30 rounded-lg font-medium hover:bg-white/30 transition-colors"
                >
                  Connect Providers
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
       */}

      {setupProgress.percentage >= 100 && recommendations.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Recommended actions to increase your AI visibility.</h2>
            </div>
            {recommendationCounts.high > 0 && (
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-red-100 text-red-800">
                {recommendationCounts.high} High Priority
              </span>
            )}
          </div>

          <div className="space-y-3">
            {recommendations.map((rec) => (
              <div
                key={rec.id}
                className={`p-4 rounded-lg border-l-4 ${
                  rec.priority === 'high'
                    ? 'border-red-500 bg-red-50'
                    : rec.priority === 'medium'
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-blue-500 bg-blue-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Lightbulb className={`h-5 w-5 ${
                        rec.priority === 'high'
                          ? 'text-red-600'
                          : rec.priority === 'medium'
                          ? 'text-yellow-600'
                          : 'text-blue-600'
                      }`} />
                      <h3 className="text-sm font-semibold text-gray-900">{rec.title}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        rec.priority === 'high'
                          ? 'bg-red-100 text-red-700'
                          : rec.priority === 'medium'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {rec.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{rec.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-600">
                      <div className="flex items-center">
                        <Zap className="h-3 w-3 mr-1" />
                        <span>{rec.estimatedImpact}</span>
                      </div>
                      {/*
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>{rec.estimatedTime}</span>
                      </div>
                      */}
                    </div>
                  </div>
                  <Link
                    to={rec.actionLink}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {rec.actionText}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts - Show based on data availability */}
      {userConfig.dataHistoryMonths >= 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AI Provider Distribution - Only show if providers configured */}
          {userConfig.aiProvidersConfigured.length > 0 ? (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Mentions by AI Provider</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={aiProviderData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {aiProviderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Mentions by AI Provider</h2>
              <div className="h-[300px] flex items-center justify-center">
                <div className="text-center">
                  <Globe className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 mb-3">Connect AI providers to see distribution</p>
                  <Link
                    to="/settings"
                    className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Configure Providers
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  )
}